import React from 'react';
import { preserveQueryParams } from './browser_helpers.js';

import {
	getPlayerWithNumber,
	ordinalSuffix,
	formatGameTeamDetails,
	formaRelativeNumberToPercentage,
	applyDefaultsForNullUndefined,
} from './shared_helpers.js';
import { generateTeamLogoAndNameComponent } from '../../containers/ancillary_sport/league_table/stats_formatting/formatting_helpers.js';

export const championsLeagueFinalRoundTypes = ['Group Stage', 'Round of 16', 'Quarterfinal', 'Semifinal', 'Final'];

export const MLSStatsPlayersCategories = [
	'goals',
	'assists',
	'shots_on_target',
	'saves',
	'save_percentage',
	'clean_sheets',
	'fouls',
	'red_cards',
	'yellow_cards',
];

export const MLSStatsTeamsCategories = [
	'goals',
	'shots_on_target',
	'average_possession',
	'saves',
	'goals_conceded',
	'clean_sheets',
	'fouls',
	'red_cards',
	'yellow_cards',
];

export function formatLeagueName(league) {
	const leagueMap = {
		fran: 'LIGUE 1',
		liga: 'LIGA',
		chlg: 'CHAMPIONS\nLEAGUE',
		bund: 'BUNDESLIGA',
		seri: 'SERIE A',
		bpl: 'BPL',
		mls: 'MLS',
		euro: 'EURO',
		world_cup: 'FIFA World Cup',
		wwc: "Women's World Cup",
		oly_msoc: 'OLY MSOC',
		oly_wsoc: 'OLY WSOC',
		eng_fa_cup: 'FA CUP',
	};
	return leagueMap[league] || '';
}

export function orderSoccerLeagues(leagues) {
	const desiredOrder = [
		'world_cup',
		'wwc',
		'eng_fa_cup',
		'oly_msoc',
		'oly_wsoc',
		'euro',
		'bpl',
		'chlg',
		'bund',
		'mls',
		'liga',
		'seri',
		'fran',
	];
	const newOrder = {};
	desiredOrder.forEach(league => {
		if (leagues[league]) {
			newOrder[league] = leagues[league];
		}
	});
	return newOrder;
}

export function getSoccerLeagueSlug(league) {
	const leagueMap = {
		'ligue-1': 'fran',
		'la-liga': 'liga',
		'champions-league': 'chlg',
		bundesliga: 'bund',
		'serie-a': 'seri',
		'premier-league': 'bpl',
		'fifa-world-cup': 'world_cup',
		'womens-world-cup': 'wwc',
	};
	if (leagueMap[league]) {
		return leagueMap[league];
	} else {
		return league;
	}
}

export function formatGameStats(homeTeam, visitingTeam, coverageLevel) {
	if (!visitingTeam.game_stats || !homeTeam.game_stats || coverageLevel < 4) {
		return [];
	}
	const statCategories = [
		{
			title: 'Shots',
			valueKey: 'shots',
		},
		{
			title: 'On Target',
			valueKey: 'shots_on_goal',
		},
		{
			title: 'Pass Accuracy %',
			valueKey: 'passes_percentage',
			valueFormatter: n => {
				return formaRelativeNumberToPercentage(n, true);
			},
		},
		{
			title: 'Corners',
			valueKey: 'corner_kicks',
		},
		{
			title: 'Crosses',
			valueKey: 'crosses',
		},
		{
			title: 'Fouls',
			valueKey: 'fouls',
		},
		{
			title: 'Tackles',
			valueKey: 'tackles',
		},
		{
			title: 'Offsides',
			valueKey: 'offsides',
		},
		{
			title: 'Yellow Cards',
			valueKey: 'yellow_cards',
		},
		{
			title: 'Red Cards',
			valueKey: 'red_cards',
		},
		{
			title: 'Possession %',
			valueKey: 'possession_percentage',
			valueFormatter: n => `${n}%`,
		},
	];

	const gameStats = statCategories.map(statCategory => {
		let homeTeamValue = homeTeam.game_stats[statCategory.valueKey];
		let visitingTeamValue = visitingTeam.game_stats[statCategory.valueKey];

		if (typeof homeTeamValue === 'string' || homeTeamValue instanceof String) {
			homeTeamValue = parseFloat(homeTeamValue) || 0;
		}
		if (typeof visitingTeamValue === 'string' || visitingTeamValue instanceof String) {
			visitingTeamValue = parseFloat(visitingTeamValue) || 0;
		}

		if ((!homeTeamValue && homeTeamValue !== 0) || (!visitingTeamValue && visitingTeamValue !== 0)) {
			return null;
		}

		if (coverageLevel < 6 && statCategory.valueKey === 'possession_percentage') {
			return null;
		}

		return {
			title: statCategory.title,
			homeTeamValue: homeTeamValue,
			visitingTeamValue: visitingTeamValue,
			valueFormatter: statCategory.valueFormatter,
		};
	});
	return gameStats.filter(n => n); // filter out null values
}

export function formatSeasonLeaders(homeTeam, visitingTeam) {
	const leaderCategories = [
		{
			title: 'Points',
			valueKey: 'points_team',
		},
		{
			title: 'Points Per Match',
			valueKey: 'pct',
		},
		{
			title: 'Goals Per Match',
			valueKey: 'goals_per_game',
		},
		{
			title: 'Goals Against Per Match',
			valueKey: 'goals_against_per_game',
		},
		{
			title: 'Goal Differential',
			valueKey: 'goal_differential',
		},
		{
			title: 'Shutouts',
			valueKey: 'shutouts',
		},
	];

	const seasonLeaders = leaderCategories.map(leaderCategory => {
		const homeTeamValue = homeTeam?.season_stats?.[leaderCategory.valueKey];
		const visitingTeamValue = visitingTeam?.season_stats?.[leaderCategory.valueKey];

		if (homeTeamValue === undefined || visitingTeamValue === undefined) {
			return null;
		}

		// Home team and visiting team are swapped because it is soccer.
		return {
			title: leaderCategory.title,
			homeTeamShortName: visitingTeam.short_name,
			homeTeamColor: visitingTeam.color,
			visitingTeamColor: homeTeam.color,
			visitingTeamShortName: homeTeam.short_name,
			homeTeamLogo: visitingTeam.image_url_90,
			visitingTeamLogo: homeTeam.image_url_90,
			homeTeamValue: visitingTeamValue,
			visitingTeamValue: homeTeamValue,
		};
	});
	return seasonLeaders.filter(n => n); // filter out null values
}

export function formatTeamStats(homeTeam, visitingTeam) {
	if (!visitingTeam.team_stats || !homeTeam.team_stats) {
		return [];
	}

	const statCategories = [
		{
			title: 'Tackles',
			valueKey: 'tackles',
		},
		{
			title: 'Fouls',
			valueKey: 'fouls',
		},

		{
			title: 'Yellow Cards',
			valueKey: 'yellow_cards',
		},
		{
			title: 'Red Cards',
			valueKey: 'red_cards',
		},
	];

	const teamStats = statCategories.map(statCategory => {
		let homeTeamValue = homeTeam.team_stats[statCategory.valueKey];
		let visitingTeamValue = visitingTeam.team_stats[statCategory.valueKey];

		if ((!homeTeamValue && homeTeamValue !== 0) || (!visitingTeamValue && visitingTeamValue !== 0)) {
			return null;
		}

		return {
			title: statCategory.title,
			homeTeamValue: homeTeamValue,
			visitingTeamValue: visitingTeamValue,
			isPercent: statCategory.isPercent,
			valueFormatter: statCategory.valueFormatter,
		};
	});
	return teamStats.filter(n => n); // filter out null values
}

export function formatSeasonRecord(team) {
	let seasonRecord = '';

	if (team.league_rank) {
		seasonRecord += `${ordinalSuffix(team.league_rank)}`;
	}

	if (team?.season_stats?.wins >= 0 && team?.season_stats?.losses >= 0 && team?.season_stats?.ties >= 0) {
		seasonRecord += ` (${team.season_stats.wins}-${team.season_stats.ties}-${team.season_stats.losses})`;
	} else if (team?.season_stats?.wins >= 0 && team?.season_stats?.losses >= 0) {
		seasonRecord += ` (${team.season_stats.wins}-${team.season_stats.losses})`;
	}

	return seasonRecord;
}

export function formatGoalSummary(periods, homeTeamId, visitingTeamId) {
	let goals = [];

	periods?.forEach(period => {
		if (period.goals) goals.push(...period.goals);
	});

	const visitingTeamGoals = goals.filter(
		goal =>
			(goal.team_id === visitingTeamId && !goal.shootout_goal && !goal.own_goal) ||
			(goal.team_id !== visitingTeamId && goal.own_goal)
	);
	const homeTeamGoals = goals.filter(
		goal =>
			(goal.team_id === homeTeamId && !goal.shootout_goal && !goal.own_goal) ||
			(goal.team_id !== homeTeamId && goal.own_goal)
	);
	return { homeTeamGoals, visitingTeamGoals };
}

export function formatGamecardData(game = {}, options = {}) {
	const gameStatus = game.game_status;
	const {
		visitingTeamName,
		visitingTeamLogo,
		homeTeamName,
		homeTeamLogo,
		broadcastName,
		broadcastString,
		gameLoser,
		gameTime,
		gameDate,
	} = formatGameTeamDetails(game);

	const matchupResult = game.series_matchups_result?.toUpperCase();
	// Temporarily commented out until backend aggregate values are fixed.
	// const aggregateText = formatAggregateText(
	//     game?.visiting_team?.aggregate_final_game || game?.home_team?.aggregate_final_game,
	//     game?.visiting_team?.aggregate_outcome_score,
	//     game?.visiting_team_shootout_goals,
	//     game?.visiting_team?.short_name,
	//     game?.home_team?.aggregate_outcome_score,
	//     game?.home_team_shootout_goals,
	//     game?.home_team?.short_name,
	//     game?.game_status
	// )

	const commonProps = {
		visitingTeamName,
		visitingTeamLogo,
		homeTeamName,
		homeTeamLogo,
		link: preserveQueryParams(`${game.new_game_url}`),
	};

	if (gameStatus === 'Pre-Game') {
		return {
			...commonProps,
			visitingTeamData: game.new_visiting_odds || '\u00A0',
			homeTeamData: game.new_home_odds || '\u00A0',
			teamDataStyle: 'odds',
			footerText: matchupResult,
			renderGameStatus: () => {
				return (
					<>
						{options.showDate ? (
							<div className='game_date'>
								{gameDate}, {gameTime}
							</div>
						) : (
							<div className='game_date'>{gameTime}</div>
						)}
						{broadcastName && <div className='broadcastName'>{broadcastName}</div>}
					</>
				);
			},
		};
	}
	if (gameStatus === 'In-Progress') {
		return {
			...commonProps,
			visitingTeamData: `${game.visiting_team?.score}`,
			homeTeamData: `${game.home_team?.score}`,
			footerText: broadcastString,
			renderGameStatus: () => {
				return (
					<>
						<div>{game.clock}</div>
					</>
				);
			},
		};
	}
	if (gameStatus === 'Final') {
		return {
			...commonProps,
			visitingTeamData: `${game.visiting_team?.score}`,
			homeTeamData: `${game.home_team?.score}`,
			loser: gameLoser,
			footerText: matchupResult,
			renderGameStatus: () => <div>{game.clock}</div>,
		};
	}

	// For games that are cancelled, postponed, forfeit, etc...
	return {
		...commonProps,
		visitingTeamData: '\u00A0',
		homeTeamData: '\u00A0',
		footerText: matchupResult,
		renderGameStatus: () => {
			return (
				<>
					<div>{gameStatus}</div>
				</>
			);
		},
	};
}

export function formatBoxscore(team = {}, details = {}) {
	const gameStatus = details.status;
	const { goalkeepers, defenders, forwards, midfielders } = applyDefaultsForNullUndefined(team, {
		goalkeepers: [],
		defenders: [],
		forwards: [],
		midfielders: [],
	});
	let columnsGoalkeepers = [],
		columnsDefenders = [],
		columnMidfielders = [],
		columnForwards = [];
	if (gameStatus === 'Final') {
		columnsGoalkeepers.push({ title: 'MIN', dataKey: 'minutes', tooltipText: 'Minutes' });
		columnsDefenders.push({ title: 'MIN', dataKey: 'minutes', tooltipText: 'Minutes' });
		columnMidfielders.push({ title: 'MIN', dataKey: 'minutes', tooltipText: 'Minutes' });
		columnForwards.push({ title: 'MIN', dataKey: 'minutes', tooltipText: 'Minutes' });
	}
	columnsGoalkeepers.push(
		{ title: 'GA', dataKey: 'goalsAgainst', tooltipText: 'Goals Against' },
		{ title: 'SV', dataKey: 'saves', tooltipText: 'Saves' },
		{ title: 'SA', dataKey: 'shotsAgainst', tooltipText: 'Shots Against' },
		{ title: 'SOT', dataKey: 'shotsOnTarget', tooltipText: 'Shots On Target' },
		{},
		{},
		{},
		{}
	);

	columnsDefenders.push(
		{ title: 'G', dataKey: 'goals', tooltipText: 'Goals' },
		{ title: 'A', dataKey: 'assists', tooltipText: 'Assists' },
		{ title: 'S', dataKey: 'shots', tooltipText: 'Shots' },
		{ title: 'SOT', dataKey: 'shotsOnTarget', tooltipText: 'Shots On Target' },
		{ title: 'PAS', dataKey: 'passes', tooltipText: 'Passes' },
		{ title: 'ACC', dataKey: 'passesPercentage', tooltipText: 'Pass Accuracy (%)' },
		{ title: 'TCK', dataKey: 'tackles', tooltipText: 'Tackles' },
		{ title: 'F', dataKey: 'fouls', tooltipText: 'Fouls' }
	);

	columnMidfielders.push(
		{ title: 'G', dataKey: 'goals', tooltipText: 'Goals' },
		{ title: 'A', dataKey: 'assists', tooltipText: 'Assists' },
		{ title: 'S', dataKey: 'shots', tooltipText: 'Shots' },
		{ title: 'SOT', dataKey: 'shotsOnTarget', tooltipText: 'Shots On Target' },
		{ title: 'PAS', dataKey: 'passes', tooltipText: 'Passes' },
		{ title: 'ACC', dataKey: 'passesPercentage', tooltipText: 'Pass Accuracy (%)' },
		{ title: 'TCK', dataKey: 'tackles', tooltipText: 'Tackles' },
		{ title: 'F', dataKey: 'fouls', tooltipText: 'Fouls' }
	);

	columnForwards.push(
		{ title: 'G', dataKey: 'goals', tooltipText: 'Goals' },
		{ title: 'A', dataKey: 'assists', tooltipText: 'Assists' },
		{ title: 'S', dataKey: 'shots', tooltipText: 'Shots' },
		{ title: 'SOT', dataKey: 'shotsOnTarget', tooltipText: 'Shots On Target' },
		{ title: 'PAS', dataKey: 'passes', tooltipText: 'Passes' },
		{ title: 'ACC', dataKey: 'passesPercentage', tooltipText: 'Pass Accuracy (%)' },
		{ title: 'TCK', dataKey: 'tackles', tooltipText: 'Tackles' },
		{ title: 'F', dataKey: 'fouls', tooltipText: 'Fouls' }
	);

	return [
		{
			title: 'Goalkeepers',
			columns: columnsGoalkeepers,
			data: goalkeepers.map(goalkeeper => {
				let val = {
					name: getPlayerWithNumber(goalkeeper),
					goalsAgainst: goalkeeper.goals_against,
					saves: goalkeeper.saves,
					shotsAgainst: goalkeeper.shots_against,
					shotsOnTarget: goalkeeper.shots_on_goal,
				};
				if (gameStatus === 'Final') {
					val.minutes = goalkeeper.minutes;
				}
				return val;
			}),
		},
		{
			title: 'Defenders',
			columns: columnsDefenders,
			data: defenders.map(defender => {
				let val = {
					name: getPlayerWithNumber(defender),

					goals: defender.goals,
					assists: defender.assists,
					shots: defender.shots,
					shotsOnTarget: defender.shots_on_goal,
					touches: defender.touches,
					passes: defender.passes,
					passesPercentage: formaRelativeNumberToPercentage(defender.passes_percentage),
					tackles: defender.tackles,
					fouls: defender.fouls,
				};
				if (gameStatus === 'Final') {
					val.minutes = defender.minutes;
				}
				return val;
			}),
		},
		{
			title: 'Midfielders',
			columns: columnMidfielders,
			data: midfielders.map(midfielder => {
				let val = {
					name: getPlayerWithNumber(midfielder),

					goals: midfielder.goals,
					assists: midfielder.assists,
					shots: midfielder.shots,
					shotsOnTarget: midfielder.shots_on_goal,
					touches: midfielder.touches,
					passes: midfielder.passes,
					passesPercentage: formaRelativeNumberToPercentage(midfielder.passes_percentage),
					tackles: midfielder.tackles,
					fouls: midfielder.fouls,
				};
				if (gameStatus === 'Final') {
					val.minutes = midfielder.minutes;
				}
				return val;
			}),
		},
		{
			title: 'Forwards',
			columns: columnForwards,
			data: forwards.map(forward => {
				let val = {
					name: getPlayerWithNumber(forward),
					minutes: forward.minutes,
					goals: forward.goals,
					assists: forward.assists,
					shots: forward.shots,
					shotsOnTarget: forward.shots_on_goal,
					touches: forward.touches,
					passes: forward.passes,
					passesPercentage: formaRelativeNumberToPercentage(forward.passes_percentage),
					tackles: forward.tackles,
					fouls: forward.fouls,
				};
				if (gameStatus === 'Final') {
					val.minutes = forward.minutes;
				}
				return val;
			}),
		},
	];
}

export function formatBoxscoreLegend(details = {}) {
	const gameStatus = details.status;
	let columnsDefMidForw = [],
		columnsGoalkeepers = [];
	if (gameStatus === 'Final') {
		columnsDefMidForw.push({ title: 'MIN', dataKey: 'minutes', tooltipText: 'Minutes' });
		columnsGoalkeepers.push({ title: 'MIN', dataKey: 'minutes', tooltipText: 'Minutes' });
	}
	columnsDefMidForw.push(
		{ title: 'G', dataKey: 'goals', tooltipText: 'Goals' },
		{ title: 'A', dataKey: 'assists', tooltipText: 'Assists' },
		{ title: 'S', dataKey: 'shots', tooltipText: 'Shots' },
		{ title: 'SOT', dataKey: 'shotsOnTarget', tooltipText: 'Shots On Target' },
		{ title: 'TOU', dataKey: 'touches', tooltipText: 'Touches' },
		{ title: 'PAS', dataKey: 'passes', tooltipText: 'Passes' },
		{ title: 'ACC', dataKey: 'passesPercentage', tooltipText: 'Pass Accuracy %' },
		{ title: 'TCK', dataKey: 'tackles', tooltipText: 'Tackles' },
		{ title: 'F', dataKey: 'fouls', tooltipText: 'Fouls' }
	);
	columnsGoalkeepers.push(
		{ title: 'GA', dataKey: 'goalsAgainst', tooltipText: 'Goals Against' },
		{ title: 'SV', dataKey: 'saves', tooltipText: 'Saves' },
		{ title: 'SA', dataKey: 'shotsAgainst', tooltipText: 'Shots Against' },
		{ title: 'SOT', dataKey: 'shotsOnTarget', tooltipText: 'Shots On Target' },
		{},
		{},
		{},
		{}
	);

	return [
		{
			title: 'Goalkeepers',
			columns: columnsGoalkeepers,
			data: [],
		},
		{
			title: 'Defenders / Midfielders / Forwards',
			columns: columnsDefMidForw,
			data: [],
		},
	];
}
export function formatRedYellow(team = {}, periods = []) {
	let teamId = team.id;
	let yellowCards = [],
		redCards = [];
	periods.forEach(item => {
		item?.cards?.forEach(card => {
			if (card && card.team_id === teamId && card.type === 'yellow') {
				yellowCards.push({
					firstName: card.first_name,
					lastName: card.last_name,
					description: card.description,
				});
			}
			if (card && card.team_id === teamId && card.type === 'red') {
				redCards.push({
					firstName: card.first_name,
					lastName: card.last_name,
					description: card.description,
				});
			}
		});
	});
	if (team.game_stats) {
		let yellowLegend = formatCardLegend(team.game_stats?.yellow_cards, yellowCards);
		let redLegend = formatCardLegend(team.game_stats?.red_cards, redCards);
		return [
			{ key: 'Yellow Cards', value: yellowLegend },
			{ key: 'Red Cards', value: redLegend },
		];
	} else return [];
}

function formatCardLegend(number, cards = []) {
	if (number === 0) return '0';
	let legend = number + ' (';
	cards.forEach(element => {
		if (element.firstName) legend = legend.concat(element.firstName.charAt(0).toUpperCase() + '. ');
		if (element.lastName) {
			legend = legend.concat(element.lastName);
		}
		if (element.description) legend = legend.concat(' - ' + element.description + ', ');
	});
	legend = legend.trim();
	if (legend.charAt(legend.length - 1) === ',') legend = legend.slice(0, legend.length - 1);
	legend = legend.concat(')');
	return legend;
}

export function formatAggregateText(
	isFinalGameOfAggregate,
	visitingTeamAggregate,
	visitingTeamShootoutGoals,
	visitingTeamShortName = '',
	homeTeamAggregate,
	homeTeamShootoutGoals,
	homeTeamShortName = '',
	gameStatus
) {
	if (isNaN(visitingTeamAggregate) || isNaN(homeTeamAggregate)) return null;
	if (typeof isFinalGameOfAggregate === 'undefined') return null;

	const verb = isFinalGameOfAggregate ? 'wins' : 'leads';

	if (gameStatus === 'Final') {
		if (visitingTeamAggregate > homeTeamAggregate) {
			return `${visitingTeamShortName.toUpperCase()} ${verb} ${visitingTeamAggregate}-${homeTeamAggregate} on agg.`;
		}

		if (visitingTeamAggregate < homeTeamAggregate) {
			return `${homeTeamShortName.toUpperCase()} ${verb} ${homeTeamAggregate}-${visitingTeamAggregate} on agg.`;
		}

		if (visitingTeamAggregate + visitingTeamShootoutGoals > homeTeamAggregate + homeTeamShootoutGoals) {
			return `${visitingTeamShortName.toUpperCase()} ${verb} ${visitingTeamAggregate}-${homeTeamAggregate} on agg./ ${visitingTeamShootoutGoals}-${homeTeamShootoutGoals} PKs`;
		}
		if (visitingTeamAggregate + visitingTeamShootoutGoals < homeTeamAggregate + homeTeamShootoutGoals) {
			return `${homeTeamShortName.toUpperCase()} ${verb} ${homeTeamAggregate}-${visitingTeamAggregate} on agg./ ${homeTeamShootoutGoals}-${visitingTeamShootoutGoals} PKs`;
		}
		return `Tied ${visitingTeamAggregate}-${homeTeamAggregate} on agg.`;
	} else {
		if (visitingTeamAggregate > homeTeamAggregate) {
			return `${visitingTeamShortName.toUpperCase()} leads ${visitingTeamAggregate}-${homeTeamAggregate} on agg.`;
		}

		if (visitingTeamAggregate < homeTeamAggregate) {
			return `${homeTeamShortName.toUpperCase()} leads ${homeTeamAggregate}-${visitingTeamAggregate} on agg.`;
		}
		return `Tied ${visitingTeamAggregate}-${homeTeamAggregate} on agg.`;
	}
}

export const processFifaBracketData = data => {
	const gameIdMap = data.reduce((acc, match) => {
		acc[match.round.cup_round_id] = match;
		return acc;
	}, {});

	const bracketData = {
		round_of_32: [],
		round_of_16: [],
		quarterfinal: [],
		semifinal: [],
		final: [],
	};
	const finalMatch = data.find(match => match.round.name === 'final');

	bracketData.final.push(finalMatch);

	// populate round games based on linked_cup_rounds relationships starting from the final match to round of 32
	finalMatch.round.linked_cup_rounds.forEach(linkedRound => {
		if (linkedRound.type === 'child') {
			bracketData.semifinal.push(gameIdMap[linkedRound.id]);
		}
	});

	bracketData.semifinal.forEach(semifinalMatch => {
		semifinalMatch.round.linked_cup_rounds.forEach(linkedRound => {
			if (linkedRound.type === 'child') {
				bracketData.quarterfinal.push(gameIdMap[linkedRound.id]);
			}
		});
	});

	bracketData.quarterfinal.forEach(quarterfinalMatch => {
		quarterfinalMatch.round.linked_cup_rounds.forEach(linkedRound => {
			if (linkedRound.type === 'child') {
				bracketData.round_of_16.push(gameIdMap[linkedRound.id]);
			}
		});
	});

	bracketData.round_of_16.forEach(r16Match => {
		r16Match.round.linked_cup_rounds.forEach(linkedRound => {
			if (linkedRound.type === 'child') {
				bracketData.round_of_32.push(gameIdMap[linkedRound.id]);
			}
		});
	});

	return bracketData;
};

export const roundTypeMap = {
	round_of_32: {
		name: 'ROUND OF 32',
		shortName: 'RND OF 32',
	},
	round_of_16: {
		name: 'ROUND OF 16',
		shortName: 'RND OF 16',
	},
	quarterfinal: {
		name: 'QUARTERFINAL',
		shortName: 'QUARTERS',
	},
	semifinal: {
		name: 'SEMIFINAL',
		shortName: 'SEMIS',
	},
	final: {
		name: 'FINAL',
		shortName: 'FINAL',
	},
};

export const getFinalScore = matchDetails => {
	const { home_team: homeTeamDetails, visiting_team: visitingTeamDetails } = matchDetails;
	const {
		overtime_score: homeOverTimeScore,
		penalty_score: homePenaltyScore,
		normaltime_score: homeNormalTimeScore,
	} = homeTeamDetails;

	const {
		overtime_score: visitingOverTimeScore,
		penalty_score: visitingPenaltyScore,
		normaltime_score: visitingNormalTimeScore,
	} = visitingTeamDetails;

	let homeScore = 0;
	let visitingScore = 0;

	let homeScoreText = '';
	let visitingScoreText = '';
	let winner = '';

	if (homePenaltyScore || visitingPenaltyScore) {
		homeScore = homeNormalTimeScore + homeOverTimeScore;
		homeScoreText = `${homeScore} (${homePenaltyScore})`;

		visitingScore = visitingNormalTimeScore + visitingOverTimeScore;
		visitingScoreText = `${visitingScore} (${visitingPenaltyScore})`;
		winner = homePenaltyScore > visitingPenaltyScore ? 'homeTeam' : 'visitingTeam';
	}
	if ((homeOverTimeScore && !homePenaltyScore) || (visitingOverTimeScore && !visitingPenaltyScore)) {
		homeScore = homeNormalTimeScore + homeOverTimeScore;
		homeScoreText = `${homeScore}`;

		visitingScore = visitingNormalTimeScore + visitingOverTimeScore;
		visitingScoreText = `${visitingScore}`;
		winner = homeScore > visitingScore ? 'homeTeam' : 'visitingTeam';
	}
	if (!homeOverTimeScore && !homePenaltyScore && !visitingOverTimeScore && !visitingPenaltyScore) {
		homeScore = homeNormalTimeScore;
		homeScoreText = `${homeScore}`;

		visitingScore = visitingNormalTimeScore;
		visitingScoreText = `${visitingScore}`;
		winner = homeScore > visitingScore ? 'homeTeam' : visitingScore > homeScore ? 'visitingTeam' : '';
	}

	return { homeScore, homeScoreText, visitingScore, visitingScoreText, winner };
};

export const getGameInfo = matchDetails => {
	const {
		game_status: gameStatus,
		timestamp: matchTimestamp,
		home_team: homeTeamDetails,
		visiting_team: visitingTeamDetails,
	} = matchDetails;
	let infoText = '';
	if (gameStatus === 'Pre-Game') {
		infoText = `${new Date(matchTimestamp * 1000).toLocaleDateString('en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
		})} @ ${new Date(matchTimestamp * 1000).toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		})}`;
	}
	if (gameStatus === 'In-Progress') {
		infoText = 'In Progress';
	}
	if (gameStatus === 'Final') {
		const { overtime_score: homeOverTimeScore, penalty_score: homePenaltyScore } = homeTeamDetails;

		const { overtime_score: visitingOverTimeScore, penalty_score: visitingPenaltyScore } = visitingTeamDetails;

		if (homePenaltyScore || visitingPenaltyScore) {
			infoText = 'Final/PK';
		} else if ((homeOverTimeScore && !homePenaltyScore) || (visitingOverTimeScore && !visitingPenaltyScore)) {
			infoText = 'Final/ET';
		} else {
			infoText = 'Final/FT';
		}
	}
	return infoText;
};

export const formatSoccerTeamsTableData = (teams, league, isMobileWidth) => {
	if (!teams || teams.length === 0) return [];
	let teamsDataObj = {};
	if (league === 'world_cup') {
		teamsDataObj['data'] = teams.map(teamDetails => {
			// position is added dynamically in the component based on sorting
			return {
				id: teamDetails.name,
				team: generateTeamLogoAndNameComponent(teamDetails.name, teamDetails.image_url_90, 'LeagueTable_teamcol'),
				goals: teamDetails.goals,
				shots_on_target: teamDetails.shots_on_target,
				average_possession: teamDetails.average_possession,
				saves: teamDetails.saves,
				goals_conceded: teamDetails.goals_conceded,
				clean_sheets: teamDetails.clean_sheets,
				fouls: teamDetails.fouls,
				red_cards: teamDetails.red_cards,
				yellow_cards: teamDetails.yellow_cards,
			};
		});

		teamsDataObj['headerValues'] = {
			position: 'POS',
			team: 'Team',
			goals: 'G',
			shots_on_target: 'SOT',
			average_possession: 'AP',
			saves: 'S',
			goals_conceded: 'GC',
			clean_sheets: 'CS',
			fouls: 'F',
			red_cards: 'RC',
			yellow_cards: 'YC',
		};

		teamsDataObj['headerHoverValues'] = {
			goals: 'GOALS',
			shots_on_target: 'SHOTS ON TARGET',
			average_possession: 'AVERAGE POSSESSION',
			saves: 'SAVES',
			goals_conceded: 'GOALS CONCEDED',
			clean_sheets: 'CLEAN SHEETS',
			fouls: 'FOULS',
			red_cards: 'RED CARDS',
			yellow_cards: 'YELLOW CARDS',
		};

		if (isMobileWidth) {
			teamsDataObj['cellWidthOverwrites'] = {
				team: 18,
			};
		} else {
			teamsDataObj['cellWidthOverwrites'] = {
				team: 30,
			};
		}

		return teamsDataObj;
	}
};
